<script setup>
import { ref, reactive, computed, onMounted } from 'vue';
import { useStore } from 'dashboard/composables/store';
import { useI18n } from 'vue-i18n';
import { useVuelidate } from '@vuelidate/core';
import { required, minLength } from '@vuelidate/validators';
import { useAlert } from 'dashboard/composables';
import {
  CONVERSATION_PERMISSIONS,
  CONVERSATION_SCOPES,
  CUSTOM_ROLE_PERMISSION_GROUPS,
  CUSTOM_ROLE_PRESETS,
} from 'dashboard/constants/permissions.js';
import { getConversationScope } from 'dashboard/helper/permissionsHelper';

import Button from 'dashboard/components-next/button/Button.vue';
import Checkbox from 'dashboard/components-next/checkbox/Checkbox.vue';
import RadioCard from 'dashboard/components-next/radioCard/RadioCard.vue';

const props = defineProps({
  mode: {
    type: String,
    default: 'add',
    validator: value => ['add', 'edit'].includes(value),
  },
  selectedRole: {
    type: Object,
    default: () => ({}),
  },
});

const emit = defineEmits(['close']);

const store = useStore();
const { t } = useI18n();

const name = ref('');
const description = ref('');
// Starts unset on purpose: 'none' is a legitimate choice, so pre-selecting it would read as a
// recommendation and let an admin save a role that sees nothing without ever deciding to.
const conversationScope = ref(null);
const otherPermissions = ref([]);

const nameInput = ref(null);

const addCustomRole = reactive({
  showLoading: false,
  message: '',
});

const selectedPermissions = computed(() => [
  ...(CONVERSATION_SCOPES[conversationScope.value] ?? []),
  ...otherPermissions.value,
]);

const rules = computed(() => ({
  name: { required, minLength: minLength(2) },
  description: { required },
  selectedPermissions: { required, minLength: minLength(1) },
}));

const v$ = useVuelidate(rules, { name, description, selectedPermissions });

const fingerprint = permissions => [...permissions].sort().join(',');

const presets = computed(() =>
  Object.keys(CUSTOM_ROLE_PRESETS).map(key => ({
    key,
    name: t(`CUSTOM_ROLE.FORM.PRESETS.${key}.NAME`),
    description: t(`CUSTOM_ROLE.FORM.PRESETS.${key}.DESCRIPTION`),
    isActive:
      fingerprint(CUSTOM_ROLE_PRESETS[key]) ===
      fingerprint(selectedPermissions.value),
  }))
);

const conversationScopes = computed(() =>
  Object.keys(CONVERSATION_SCOPES).map(scope => ({
    id: scope,
    label: t(
      `CUSTOM_ROLE.FORM.CONVERSATION_SCOPE.${scope.toUpperCase()}.LABEL`
    ),
    description: t(
      `CUSTOM_ROLE.FORM.CONVERSATION_SCOPE.${scope.toUpperCase()}.DESCRIPTION`
    ),
  }))
);

const permissionGroups = computed(() =>
  Object.keys(CUSTOM_ROLE_PERMISSION_GROUPS).map(key => ({
    key,
    name: t(`CUSTOM_ROLE.FORM.PERMISSION_GROUPS.${key}`),
    permissions: CUSTOM_ROLE_PERMISSION_GROUPS[key],
  }))
);

const splitPermissions = permissions => {
  conversationScope.value = getConversationScope(permissions);
  otherPermissions.value = permissions.filter(
    permission => !CONVERSATION_PERMISSIONS.includes(permission)
  );
};

const resetForm = () => {
  name.value = '';
  description.value = '';
  conversationScope.value = null;
  otherPermissions.value = [];
  v$.value.$reset();
};

const populateEditForm = () => {
  name.value = props.selectedRole.name || '';
  description.value = props.selectedRole.description || '';
  splitPermissions(props.selectedRole.permissions || []);
};

const applyPreset = presetKey => {
  splitPermissions(CUSTOM_ROLE_PRESETS[presetKey]);
  // Only a starting point: a name the admin already typed is theirs to keep.
  if (!name.value) name.value = t(`CUSTOM_ROLE.FORM.PRESETS.${presetKey}.NAME`);
  if (!description.value) {
    description.value = t(`CUSTOM_ROLE.FORM.PRESETS.${presetKey}.DESCRIPTION`);
  }
};

const togglePermission = (permission, isChecked) => {
  otherPermissions.value = isChecked
    ? [...otherPermissions.value, permission]
    : otherPermissions.value.filter(item => item !== permission);
};

onMounted(() => {
  if (props.mode === 'edit') {
    populateEditForm();
  }
  // Focus the name input when mounted
  nameInput.value?.focus();
});

const getTranslationKey = base => {
  return props.mode === 'edit'
    ? `CUSTOM_ROLE.EDIT.${base}`
    : `CUSTOM_ROLE.ADD.${base}`;
};

const modalTitle = computed(() => t(getTranslationKey('TITLE')));
const modalDescription = computed(() => t(getTranslationKey('DESC')));
const submitButtonText = computed(() => t(getTranslationKey('SUBMIT')));

const handleCustomRole = async () => {
  v$.value.$touch();
  if (v$.value.$invalid) return;

  addCustomRole.showLoading = true;
  try {
    const roleData = {
      name: name.value,
      description: description.value,
      permissions: selectedPermissions.value,
    };

    if (props.mode === 'edit') {
      await store.dispatch('customRole/updateCustomRole', {
        id: props.selectedRole.id,
        ...roleData,
      });
      useAlert(t('CUSTOM_ROLE.EDIT.API.SUCCESS_MESSAGE'));
    } else {
      await store.dispatch('customRole/createCustomRole', roleData);
      useAlert(t('CUSTOM_ROLE.ADD.API.SUCCESS_MESSAGE'));
    }

    resetForm();
    emit('close');
  } catch (error) {
    const errorMessage =
      error?.message || t(`CUSTOM_ROLE.FORM.API.ERROR_MESSAGE`);
    useAlert(errorMessage);
  } finally {
    addCustomRole.showLoading = false;
  }
};

const isSubmitDisabled = computed(
  () => v$.value.$invalid || addCustomRole.showLoading
);
</script>

<template>
  <div class="flex flex-col h-auto overflow-auto">
    <woot-modal-header
      :header-title="modalTitle"
      :header-content="modalDescription"
    />
    <form class="flex flex-col w-full gap-6" @submit.prevent="handleCustomRole">
      <div class="flex flex-col gap-2">
        <span class="text-heading-3 text-n-slate-12">
          {{ $t('CUSTOM_ROLE.FORM.PRESETS.LABEL') }}
        </span>
        <span class="text-body-main text-n-slate-11">
          {{ $t('CUSTOM_ROLE.FORM.PRESETS.HELP') }}
        </span>
        <div class="flex flex-wrap gap-2 mt-1">
          <Button
            v-for="preset in presets"
            :key="preset.key"
            v-tooltip.top="preset.description"
            type="button"
            size="sm"
            :variant="preset.isActive ? 'solid' : 'faded'"
            :color="preset.isActive ? 'blue' : 'slate'"
            :label="preset.name"
            @click="applyPreset(preset.key)"
          />
        </div>
      </div>

      <div class="grid grid-cols-1 gap-x-4 md:grid-cols-2">
        <label :class="{ error: v$.name.$error }">
          {{ $t('CUSTOM_ROLE.FORM.NAME.LABEL') }}
          <input
            ref="nameInput"
            v-model.trim="name"
            type="text"
            :placeholder="$t('CUSTOM_ROLE.FORM.NAME.PLACEHOLDER')"
            @blur="v$.name.$touch"
          />
        </label>

        <label :class="{ error: v$.description.$error }">
          {{ $t('CUSTOM_ROLE.FORM.DESCRIPTION.LABEL') }}
          <input
            v-model.trim="description"
            type="text"
            :placeholder="$t('CUSTOM_ROLE.FORM.DESCRIPTION.PLACEHOLDER')"
            @blur="v$.description.$touch"
          />
        </label>
      </div>

      <div class="flex flex-col gap-3">
        <div class="flex flex-col gap-1">
          <span class="text-heading-3 text-n-slate-12">
            {{ $t('CUSTOM_ROLE.FORM.CONVERSATION_SCOPE.LABEL') }}
          </span>
          <span class="text-body-main text-n-slate-11">
            {{ $t('CUSTOM_ROLE.FORM.CONVERSATION_SCOPE.HELP') }}
          </span>
        </div>
        <div class="grid grid-cols-1 gap-3 md:grid-cols-2">
          <RadioCard
            v-for="scope in conversationScopes"
            :key="scope.id"
            :id="scope.id"
            :label="scope.label"
            :description="scope.description"
            :is-active="conversationScope === scope.id"
            @select="conversationScope = $event"
          />
        </div>
      </div>

      <div class="flex flex-col gap-3">
        <span
          class="text-heading-3"
          :class="
            v$.selectedPermissions.$error ? 'text-n-ruby-9' : 'text-n-slate-12'
          "
        >
          {{ $t('CUSTOM_ROLE.FORM.PERMISSIONS.LABEL') }}
        </span>
        <div class="grid grid-cols-1 gap-6 md:grid-cols-3">
          <div
            v-for="group in permissionGroups"
            :key="group.key"
            class="flex flex-col gap-3"
          >
            <span class="text-label-small uppercase text-n-slate-10">
              {{ group.name }}
            </span>
            <label
              v-for="permission in group.permissions"
              :key="permission"
              class="flex items-start gap-2 !mb-0 cursor-pointer"
            >
              <span class="pt-0.5">
                <Checkbox
                  :model-value="otherPermissions.includes(permission)"
                  @update:model-value="togglePermission(permission, $event)"
                />
              </span>
              <span class="text-body-main font-normal text-n-slate-12">
                {{ $t(`CUSTOM_ROLE.PERMISSIONS.${permission.toUpperCase()}`) }}
              </span>
            </label>
          </div>
        </div>
      </div>

      <div class="flex flex-row justify-end w-full gap-2 px-0">
        <Button
          faded
          slate
          type="reset"
          :label="$t('CUSTOM_ROLE.FORM.CANCEL_BUTTON_TEXT')"
          @click.prevent="emit('close')"
        />
        <Button
          type="submit"
          :label="submitButtonText"
          :disabled="isSubmitDisabled"
          :is-loading="addCustomRole.showLoading"
        />
      </div>
    </form>
  </div>
</template>
